import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  Search, 
  RefreshCcw, 
  Package, 
  CheckCircle, 
  Clock, 
  Truck, 
  Eye,
  DollarSign,
  FileText,
  Building2,
  Download,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RawMaterialOrders = ({ franchises = [] }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_orders: 0,
    pending_payments: 0,
    pending_deliveries: 0,
    total_revenue: 0
  });
  const [filters, setFilters] = useState({
    franchise_id: 'all',
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date
      };
      if (filters.franchise_id !== 'all') {
        params.franchise_id = filters.franchise_id;
      }
      
      const response = await axios.get(`${API}/raw-material-orders`, { params });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/raw-material-orders/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [fetchOrders, fetchStats]);

  const updateOrderStatus = async (orderId, paymentStatus, deliveryStatus) => {
    try {
      await axios.put(`${API}/admin/raw-material-orders/status`, {
        order_id: orderId,
        payment_status: paymentStatus,
        delivery_status: deliveryStatus
      });
      toast.success('Order status updated');
      fetchOrders();
      fetchStats();
      
      if (selectedOrder?.order_id === orderId) {
        setSelectedOrder(prev => ({
          ...prev,
          payment_status: paymentStatus || prev.payment_status,
          delivery_status: deliveryStatus || prev.delivery_status
        }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const downloadPDF = (base64Data, orderId) => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Order_${orderId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async (orderId) => {
    try {
      setDownloadingPdf(true);
      const response = await axios.get(`${API}/raw-material-orders/${orderId}/pdf`);
      
      if (response.data.success && response.data.pdf_base64) {
        downloadPDF(response.data.pdf_base64, orderId);
        toast.success('PDF downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getStatusBadge = (status, type) => {
    const configs = {
      payment: {
        'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
        'Paid': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
        'Partially Paid': { bg: 'bg-orange-100', text: 'text-orange-700', icon: DollarSign }
      },
      delivery: {
        'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
        'Delivered': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
        'In Transit': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Truck }
      }
    };
    
    const config = configs[type]?.[status] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-3 w-3" />
        {status}
      </span>
    );
  };

  const filteredOrders = orders.filter(order => 
    order.order_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    order.franchise_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-primary">Raw Material Orders</h2>
          <p className="text-secondary mt-1">Track and manage franchise raw material orders</p>
        </div>
        <Button
          variant="outline"
          onClick={() => { fetchOrders(); fetchStats(); }}
          className="rounded-full"
        >
          <RefreshCcw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600">Total Orders</p>
                <p className="text-2xl font-bold text-blue-700">{stats.total_orders}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500 rounded-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-yellow-600">Pending Payments</p>
                <p className="text-2xl font-bold text-yellow-700">{stats.pending_payments}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500 rounded-lg">
                <Truck className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-orange-600">Pending Delivery</p>
                <p className="text-2xl font-bold text-orange-700">{stats.pending_deliveries}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-600">Total Revenue</p>
                <p className="text-2xl font-bold text-green-700">₹{stats.total_revenue?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Franchise</Label>
              <Select
                value={filters.franchise_id}
                onValueChange={(value) => setFilters(prev => ({ ...prev, franchise_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Franchises" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Franchises</SelectItem>
                  {franchises.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-secondary">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No orders found</p>
            <p className="text-sm text-muted-foreground mt-1">Orders from franchises will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => (
            <Card key={order.order_id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-primary text-lg">{order.order_id}</span>
                      <span className="text-sm text-muted-foreground">•</span>
                      <span className="text-sm text-muted-foreground">{formatDate(order.timestamp)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-secondary">
                      <Building2 className="h-4 w-4" />
                      <span>{order.franchise_name}</span>
                      <span className="text-muted-foreground">|</span>
                      <span>{order.location}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {order.ordered_items?.length || 0} items • Total: <span className="font-bold text-primary">₹{order.total_amount?.toLocaleString() || 0}</span>
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Status Badges */}
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-muted-foreground">Payment:</span>
                      {getStatusBadge(order.payment_status || 'Pending', 'payment')}
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-muted-foreground">Delivery:</span>
                      {getStatusBadge(order.delivery_status || 'Pending', 'delivery')}
                    </div>

                    {/* Quick Actions */}
                    <div className="flex gap-2">
                      {order.payment_status !== 'Paid' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-200 hover:bg-green-50"
                          onClick={() => updateOrderStatus(order.order_id, 'Paid', null)}
                        >
                          Mark Paid
                        </Button>
                      )}
                      {order.delivery_status !== 'Delivered' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          onClick={() => updateOrderStatus(order.order_id, null, 'Delivered')}
                        >
                          Mark Delivered
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setSelectedOrder(order); setIsDetailsOpen(true); }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Order Details Modal */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary">Order Details</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Order ID</p>
                  <p className="font-bold text-primary">{selectedOrder.order_id}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date & Time</p>
                  <p className="font-medium">{formatDate(selectedOrder.timestamp)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Franchise</p>
                  <p className="font-medium">{selectedOrder.franchise_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="font-medium">{selectedOrder.location}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contact</p>
                  <p className="font-medium">{selectedOrder.contact_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="font-bold text-primary text-xl">₹{selectedOrder.total_amount?.toLocaleString()}</p>
                </div>
              </div>

              {/* Status Update */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <Label>Payment Status</Label>
                  <Select
                    value={selectedOrder.payment_status || 'Pending'}
                    onValueChange={(value) => updateOrderStatus(selectedOrder.order_id, value, null)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Partially Paid">Partially Paid</SelectItem>
                      <SelectItem value="Paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Delivery Status</Label>
                  <Select
                    value={selectedOrder.delivery_status || 'Pending'}
                    onValueChange={(value) => updateOrderStatus(selectedOrder.order_id, null, value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="In Transit">In Transit</SelectItem>
                      <SelectItem value="Delivered">Delivered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Ordered Items */}
              <div>
                <h4 className="font-bold text-primary mb-3">Ordered Items</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Item</th>
                        <th className="text-left p-3 text-sm font-medium">Size/Qty</th>
                        <th className="text-right p-3 text-sm font-medium">Qty</th>
                        <th className="text-right p-3 text-sm font-medium">Price</th>
                        <th className="text-right p-3 text-sm font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedOrder.ordered_items?.map((item, idx) => (
                        <tr key={idx}>
                          <td className="p-3 font-medium">{item.item_name}</td>
                          <td className="p-3 text-secondary">{item.size_or_quantity}</td>
                          <td className="p-3 text-right">{item.quantity}</td>
                          <td className="p-3 text-right">₹{item.price_per_unit?.toFixed(2)}</td>
                          <td className="p-3 text-right font-medium">₹{item.total_price?.toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="bg-primary/5 font-bold">
                        <td colSpan={4} className="p-3 text-right">Grand Total:</td>
                        <td className="p-3 text-right text-primary">₹{selectedOrder.total_amount?.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Additional Requirements */}
              {selectedOrder.additional_requirements && (
                <div>
                  <h4 className="font-bold text-primary mb-2">Additional Requirements</h4>
                  <p className="text-secondary p-3 bg-amber-50 rounded-lg border border-amber-200">
                    {selectedOrder.additional_requirements}
                  </p>
                </div>
              )}

              {/* Download PDF Button */}
              <Button
                className="w-full"
                onClick={() => handleDownloadPdf(selectedOrder.order_id)}
                disabled={downloadingPdf}
              >
                {downloadingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download Order PDF
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RawMaterialOrders;
